"use client";
import useSWR from "swr";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { TransactionFilters, TransactionWithCategory } from "@/types";
import { transactionFiltersFromSearchParams } from "@/utils/transactionUrlFilters";
import { cfApi, cfFetcher } from "../api/apiClient";

interface TransactionsResponse {
  transactions: TransactionWithCategory[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

function buildUrl(filters: TransactionFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.categoryId) params.set("categoryId", filters.categoryId);
  if (filters.subcategoryId) params.set("subcategoryId", filters.subcategoryId);
  if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.set("dateTo", filters.dateTo);
  if (filters.type) params.set("type", filters.type);
  if (filters.currency) params.set("currency", filters.currency);
  if (filters.page) params.set("page", String(filters.page));
  if (filters.pageSize) params.set("pageSize", String(filters.pageSize));
  return cfApi(`/transactions?${params.toString()}`);
}

export function useTransactions(initialFilters: TransactionFilters = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  /** `useSearchParams()` may return a new object each render; sync only when the query string changes */
  const searchParamsSerialized = searchParams.toString();

  const [filters, setFilters] = useState<TransactionFilters>({
    page: 1,
    pageSize: 50,
    ...initialFilters,
  });

  useEffect(() => {
    const params = new URLSearchParams(searchParamsSerialized);
    const parsed = transactionFiltersFromSearchParams(params);
    setFilters((prev) => ({
      ...prev,
      ...parsed,
      dateFrom: params.has("dateFrom") ? parsed.dateFrom : undefined,
      dateTo: params.has("dateTo") ? parsed.dateTo : undefined,
      currency: params.has("currency") ? parsed.currency : undefined,
      type: params.has("type") ? parsed.type : undefined,
      categoryId: params.has("categoryId") ? parsed.categoryId : undefined,
      subcategoryId: params.has("subcategoryId")
        ? parsed.subcategoryId
        : undefined,
      search: params.has("search") ? parsed.search : undefined,
      page: 1,
    }));
  }, [searchParamsSerialized]);

  const requestUrl = buildUrl(filters);

  const { data, error, isLoading, mutate } = useSWR<TransactionsResponse>(
    requestUrl,
    cfFetcher,
    { keepPreviousData: false },
  );

  function updateFilter(
    key: keyof TransactionFilters,
    value: string | number | undefined,
  ) {
    setFilters((prev) => {
      return {
        ...prev,
        [key]: value,
        page: key === "page" ? (value as number) : 1,
      };
    });
  }

  function commitDateRange(dateFrom?: string, dateTo?: string) {
    setFilters((prev) => ({
      ...prev,
      dateFrom,
      dateTo,
      page: 1,
    }));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (dateFrom) next.set("dateFrom", dateFrom);
        else next.delete("dateFrom");
        if (dateTo) next.set("dateTo", dateTo);
        else next.delete("dateTo");
        return next;
      },
      { replace: true },
    );
  }

  return {
    transactions: data?.transactions ?? [],
    total: data?.total ?? 0,
    totalPages: data?.totalPages ?? 0,
    page: filters.page ?? 1,
    isLoading,
    error,
    filters,
    updateFilter,
    commitDateRange,
    mutate,
  };
}
